import { TOTP, Secret } from 'otpauth';
import QRCode from 'qrcode';
import crypto from 'crypto';
import logger from '../utils/logger';

/**
 * MFA（多要素認証）関連の型定義
 */
export interface MFASecret {
  secret: string;
  backupCodes: string[];
  qrCodeUrl: string;
}

export interface MFASetupResponse {
  secretKey: string;
  qrCodeDataUrl: string;
  backupCodes: string[];
  manualEntryKey: string;
}

export interface MFAVerifyRequest {
  userId: string;
  token: string;
  isBackupCode?: boolean;
}

export interface MFAVerifyResponse {
  success: boolean;
  message: string;
  backupCodesRemaining?: number;
}

export interface MFAStatus {
  isEnabled: boolean;
  backupCodesRemaining: number;
  lastUsed?: Date;
  setupDate?: Date;
}

export interface MFAConfig {
  issuer: string;
  label: string;
  algorithm: string;
  digits: number;
  period: number;
}

/**
 * MFAサービスクラス
 * TOTP（Time-based One-Time Password）とバックアップコードを管理
 */
export class MFAService {
  private static readonly DEFAULT_CONFIG: MFAConfig = {
    issuer: 'SalonHub',
    label: 'SalonHub Account',
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
  };

  private static readonly BACKUP_CODES_COUNT = 10;
  private static readonly BACKUP_CODE_LENGTH = 8;

  /**
   * 新しいMFAシークレットを生成
   * @param userId ユーザーID
   * @param userEmail ユーザーメールアドレス
   * @returns MFAセットアップ情報
   */
  static async generateMFASecret(
    userId: string,
    userEmail: string
  ): Promise<MFASetupResponse> {
    try {
      logger.log('🔐 [MFAService] MFAシークレット生成開始:', { userId, userEmail });

      // ランダムシークレット生成
      const secret = new Secret({ size: 32 });
      
      // TOTP設定
      const totp = new TOTP({
        issuer: this.DEFAULT_CONFIG.issuer,
        label: userEmail,
        algorithm: this.DEFAULT_CONFIG.algorithm,
        digits: this.DEFAULT_CONFIG.digits,
        period: this.DEFAULT_CONFIG.period,
        secret: secret,
      });

      // QRコード用URL生成
      const qrCodeUrl = totp.toString();
      
      // QRコードのData URL生成
      const qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, {
        errorCorrectionLevel: 'M',
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
        width: 256,
      });

      // バックアップコード生成
      const backupCodes = this.generateBackupCodes();

      // 手動入力用のシークレットキー（スペース区切り）
      const manualEntryKey = secret.base32.match(/.{1,4}/g)?.join(' ') || secret.base32;

      logger.log('✅ [MFAService] MFAシークレット生成完了:', { 
        userId, 
        backupCodesCount: backupCodes.length 
      });

      return {
        secretKey: secret.base32,
        qrCodeDataUrl,
        backupCodes,
        manualEntryKey,
      };
    } catch (error) {
      logger.error('❌ [MFAService] MFAシークレット生成エラー:', { error, userId });
      throw new Error('MFAシークレットの生成に失敗しました');
    }
  }

  /**
   * TOTPトークンを検証
   * @param secretKey MFAシークレットキー
   * @param token ユーザー入力のトークン
   * @param window 許容時間窓（前後の時間枠）
   * @returns 検証結果
   */
  static verifyTOTP(
    secretKey: string,
    token: string,
    window: number = 1
  ): boolean {
    try {
      logger.log('🔐 [MFAService] TOTP検証開始:', { 
        tokenLength: token.length,
        window 
      });

      // トークンバリデーション
      if (!token || !/^\d{6}$/.test(token)) {
        logger.warn('⚠️ [MFAService] 無効なトークン形式:', { token });
        return false;
      }

      // TOTP生成
      const totp = new TOTP({
        issuer: this.DEFAULT_CONFIG.issuer,
        algorithm: this.DEFAULT_CONFIG.algorithm,
        digits: this.DEFAULT_CONFIG.digits,
        period: this.DEFAULT_CONFIG.period,
        secret: Secret.fromBase32(secretKey),
      });

      // 現在時刻での検証
      const currentTime = Date.now();
      const isValid = totp.validate({ 
        token, 
        timestamp: currentTime,
        window 
      }) !== null;

      logger.log(isValid ? '✅' : '❌', '[MFAService] TOTP検証結果:', { 
        isValid,
        timestamp: new Date(currentTime).toISOString()
      });

      return isValid;
    } catch (error) {
      logger.error('❌ [MFAService] TOTP検証エラー:', { error, tokenLength: token?.length });
      return false;
    }
  }

  /**
   * バックアップコードを生成
   * @returns バックアップコード配列
   */
  static generateBackupCodes(): string[] {
    try {
      logger.log('🔐 [MFAService] バックアップコード生成開始');

      const backupCodes: string[] = [];
      
      for (let i = 0; i < this.BACKUP_CODES_COUNT; i++) {
        const code = crypto
          .randomBytes(this.BACKUP_CODE_LENGTH / 2)
          .toString('hex')
          .toUpperCase();
        
        // 4桁-4桁の形式にフォーマット
        const formattedCode = `${code.slice(0, 4)}-${code.slice(4, 8)}`;
        backupCodes.push(formattedCode);
      }

      logger.log('✅ [MFAService] バックアップコード生成完了:', { 
        count: backupCodes.length 
      });

      return backupCodes;
    } catch (error) {
      logger.error('❌ [MFAService] バックアップコード生成エラー:', error);
      throw new Error('バックアップコードの生成に失敗しました');
    }
  }

  /**
   * バックアップコードを検証
   * @param inputCode ユーザー入力のコード
   * @param validCodes 有効なバックアップコード配列
   * @returns 検証結果と残りコード数
   */
  static verifyBackupCode(
    inputCode: string,
    validCodes: string[]
  ): { isValid: boolean; remainingCodes: string[]; used?: string } {
    try {
      logger.log('🔐 [MFAService] バックアップコード検証開始:', { 
        inputCodeLength: inputCode?.length,
        validCodesCount: validCodes.length 
      });

      // 入力コードの正規化（大文字変換、ハイフン統一）
      const normalizedInput = inputCode?.toUpperCase().replace(/[-\s]/g, '-');
      
      if (!normalizedInput || !/^[A-F0-9]{4}-[A-F0-9]{4}$/.test(normalizedInput)) {
        logger.warn('⚠️ [MFAService] 無効なバックアップコード形式:', { inputCode });
        return { isValid: false, remainingCodes: validCodes };
      }

      // コード存在確認
      const codeIndex = validCodes.findIndex(
        code => code.toUpperCase() === normalizedInput
      );

      if (codeIndex === -1) {
        logger.warn('⚠️ [MFAService] バックアップコードが見つかりません:', { normalizedInput });
        return { isValid: false, remainingCodes: validCodes };
      }

      // 使用済みコードを除去
      const remainingCodes = validCodes.filter((_, index) => index !== codeIndex);
      const usedCode = validCodes[codeIndex];

      logger.log('✅ [MFAService] バックアップコード検証成功:', { 
        usedCode,
        remainingCount: remainingCodes.length 
      });

      return { 
        isValid: true, 
        remainingCodes, 
        used: usedCode 
      };
    } catch (error) {
      logger.error('❌ [MFAService] バックアップコード検証エラー:', { error, inputCode });
      return { isValid: false, remainingCodes: validCodes };
    }
  }

  /**
   * MFA設定の妥当性を検証
   * @param secretKey MFAシークレットキー
   * @param verificationToken 検証用トークン
   * @returns 検証結果
   */
  static validateMFASetup(secretKey: string, verificationToken: string): boolean {
    try {
      logger.log('🔐 [MFAService] MFA設定検証開始');

      if (!secretKey || !verificationToken) {
        logger.warn('⚠️ [MFAService] 必須パラメータが不足');
        return false;
      }

      const isValid = this.verifyTOTP(secretKey, verificationToken, 2);
      
      logger.log(isValid ? '✅' : '❌', '[MFAService] MFA設定検証結果:', { isValid });
      
      return isValid;
    } catch (error) {
      logger.error('❌ [MFAService] MFA設定検証エラー:', error);
      return false;
    }
  }

  /**
   * 現在のTOTPトークンを生成（テスト/デバッグ用）
   * @param secretKey MFAシークレットキー
   * @returns 現在のTOTPトークン
   */
  static generateCurrentTOTP(secretKey: string): string {
    try {
      const totp = new TOTP({
        issuer: this.DEFAULT_CONFIG.issuer,
        algorithm: this.DEFAULT_CONFIG.algorithm,
        digits: this.DEFAULT_CONFIG.digits,
        period: this.DEFAULT_CONFIG.period,
        secret: Secret.fromBase32(secretKey),
      });

      const token = totp.generate();
      logger.log('🔐 [MFAService] 現在のTOTP生成:', { tokenLength: token.length });
      
      return token;
    } catch (error) {
      logger.error('❌ [MFAService] TOTP生成エラー:', error);
      throw new Error('TOTPトークンの生成に失敗しました');
    }
  }

  /**
   * MFAの設定情報をクリア（セキュアな削除）
   * @param mfaData 削除対象のMFAデータ
   */
  static securelyDeleteMFAData(mfaData: any): void {
    try {
      logger.log('🔐 [MFAService] MFAデータの安全な削除開始');

      // オブジェクトのプロパティを安全に削除
      if (mfaData && typeof mfaData === 'object') {
        Object.keys(mfaData).forEach(key => {
          if (typeof mfaData[key] === 'string') {
            // 文字列の場合はランダム文字列で上書き
            mfaData[key] = crypto.randomBytes(32).toString('hex');
          }
          delete mfaData[key];
        });
      }

      logger.log('✅ [MFAService] MFAデータの安全な削除完了');
    } catch (error) {
      logger.error('❌ [MFAService] MFAデータ削除エラー:', error);
    }
  }
}

export default MFAService;